import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, getStoredToken, setStoredToken, registerUnauthorizedHandler, refreshAccessToken, VIEW_AS_USER_ID, isTemporaryError, retryLoadDelay } from "../lib/apiClient";
import { CurrentUser } from "../lib/types";
import type { TwoFactorChallenge } from "../api/twoStep";
import { signInLocation } from "../lib/geo";

// Comfortably under the backend's default 30-minute access token TTL, so an
// active tab renews before the token would ever expire — the reactive 401
// retry in apiClient.ts is just the fallback if this timer drifts or the tab
// was asleep. Runs in the background even while the tab is hidden, which is
// what keeps a session alive without the user having to touch anything.
const PROACTIVE_REFRESH_MS = 20 * 60 * 1000;

interface AuthContextValue {
  user: CurrentUser | null;
  loading: boolean;
  /** request 1239: resolves with the second-step challenge when one is due */
  login: (email: string, password: string) => Promise<{ twoFactor?: TwoFactorChallenge }>;
  finishLogin: (accessToken: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshMe: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchMe = useCallback(async () => {
    const token = getStoredToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    // request 1278: a busy or restarting server doesn't sign anyone out — wait and try again, then keep the sign-in and whoever is on screen
    for (let attempt = 0; ; attempt++) {
      try {
        const res = await api.get("/auth/me");
        setUser(res.data.data);
        break;
      } catch (err) {
        if (isTemporaryError(err)) {
          if (attempt >= 6) break;
          await new Promise((resolve) => setTimeout(resolve, retryLoadDelay(attempt)));
          continue;
        }
        if (!VIEW_AS_USER_ID) setStoredToken(null); // request 1181: a viewer frame must never sign the main window out
        setUser(null);
        break;
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    registerUnauthorizedHandler(() => {
      setUser(null);
      navigate("/login-portal-uae2026", { replace: true });
    });
    fetchMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    if (user && !VIEW_AS_USER_ID) {
      refreshTimerRef.current = setInterval(() => {
        refreshAccessToken();
        // Also re-pulls roles/permissions/moduleAccess — an admin can change
        // someone's role while their tab stays open for hours (this app has
        // no realtime push), and without this the only other way those
        // take effect is a full reload or logging out and back in. Found
        // this gap investigating a report where a Management user's Submit-
        // Quotation section wasn't showing — the DB/permission logic was
        // provably correct, so the most likely explanation was a stale
        // in-memory `user` object from before the role was granted.
        fetchMe();
      }, PROACTIVE_REFRESH_MS);
    }
    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    };
  }, [user, fetchMe]);

  const login = useCallback(
    async (email: string, password: string) => {
      const res = await api.post("/auth/login", { email, password, location: signInLocation() }); // request 1245
      if (res.data.data.twoFactor) return { twoFactor: res.data.data.twoFactor as TwoFactorChallenge };
      setStoredToken(res.data.data.accessToken);
      await fetchMe();
      return {};
    },
    [fetchMe]
  );

  const finishLogin = useCallback(
    async (accessToken: string) => {
      setStoredToken(accessToken);
      await fetchMe();
    },
    [fetchMe]
  );

  const logout = useCallback(async () => {
    try {
      await api.post("/work-time/pause").catch(() => undefined);
      await api.post("/auth/logout");
    } finally {
      setStoredToken(null);
      setUser(null);
      navigate("/login-portal-uae2026", { replace: true });
    }
  }, [navigate]);

  return <AuthContext.Provider value={{ user, loading, login, finishLogin, logout, refreshMe: fetchMe }}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
